/**
 * A builder form that stays folded until someone asks for it.
 *
 * ── Why this exists ─────────────────────────────────────────────────────
 *
 * Four authoring forms in the platform console sat permanently open beneath the
 * records they create — promo grants and credit adjustments on a tenant's Plan
 * & billing tab, promo-code creation and campaign revoke on Operations. Each is
 * an OCCASIONAL act on a screen whose everyday job is reading: on the tenant tab
 * the two forms were 60% of the tab's height, and the payment record they matter
 * to had scrolled off the top before the operator reached them.
 *
 * The cost of always-open is not only length. A form that is always there reads
 * as part of the page's state rather than as an action, which is exactly the
 * wrong impression for the one on Operations whose buttons revoke a whole
 * campaign.
 *
 * ── Why a disclosure and not a Modal ────────────────────────────────────
 *
 * `Modal` exists and is the right shell for a short, decisive question
 * ("Apply Default to existing tenants?"). These four are not that: the promo
 * forms carry a repeating effect builder that grows a row at a time, and the
 * campaign one now renders a match preview it has to keep on screen beside the
 * buttons it arms. A dialog would put both inside a nested scroller and take
 * away the record the operator is checking their entry against.
 *
 * So: an inline disclosure, with the semantics a disclosure owes —
 * `aria-expanded`, `aria-controls`, and focus moved into the panel on open so a
 * keyboard user is not left on the trigger with the fields below them.
 * Collapsing returns focus to the trigger for the same reason.
 *
 * ── What it deliberately does NOT do ────────────────────────────────────
 *
 * It does not unmount the panel's children when closed — it renders nothing at
 * all. Callers keep their own draft state, so a form that is closed and reopened
 * starts clean, which is the right default for "create one of these": a
 * half-typed grant surviving out of sight is how the wrong tenant gets credited.
 * Where a caller wants stickiness it should hold the draft above this component.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

export interface ActionDisclosureProps {
  /** The trigger's label when closed, e.g. "Give this tenant credit". Say what
   *  opening it lets you DO, not what the panel is called. */
  label: ReactNode
  /** The trigger's label when open. Defaults to the same string — most callers
   *  want "Cancel"-ish copy here. */
  openLabel?: ReactNode
  /** One line under the trigger, always visible. This is where a caller says
   *  what the action costs, so it is readable without opening anything. */
  hint?: ReactNode
  children: ReactNode
  /** Start expanded. For the rare case where the form IS the screen. */
  defaultOpen?: boolean
  /** Extra classes on the wrapper. */
  className?: string
  /** Marks the trigger as a destructive entry point (campaign revoke). Styling
   *  only — the confirmation still belongs to the caller. */
  tone?: 'default' | 'danger'
  /** Told on every open / close — for a caller that keeps the open state (e.g.
   *  across a phone/desktop layout swap, #897, fed back via `defaultOpen`). */
  onOpenChange?: (open: boolean) => void
}

export function ActionDisclosure({
  label, openLabel, hint, children, defaultOpen = false, className = '', tone = 'default', onOpenChange,
}: ActionDisclosureProps) {
  const [open, setOpen] = useState(defaultOpen)
  const panelId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  /**
   * The PREVIOUS value of `open`, so focus moves on a TRANSITION and never on a
   * render that merely re-ran.
   *
   * This started life as a `mounted` boolean and was wrong under `<StrictMode>`,
   * which is on in `apps/web/src/main.tsx`. StrictMode double-invokes effects
   * (mount → cleanup → mount), so the second run found `mounted.current === true`
   * with `open === false` and called `.focus()` on the trigger — every disclosure
   * on the page grabbed focus as it mounted, last one winning. It is visible in
   * an a11y snapshot of Operations: "End a campaign … focused", with nothing
   * clicked. Comparing against the previous VALUE is idempotent, so a repeated
   * effect run is a no-op rather than an action.
   */
  const wasOpen = useRef(defaultOpen)

  useEffect(() => {
    if (wasOpen.current === open) return
    wasOpen.current = open
    onOpenChange?.(open)
    if (open) {
      const panel = panelRef.current
      const first = panel?.querySelector<HTMLElement>(
        'input:not([disabled]),select:not([disabled]),textarea:not([disabled]),button:not([disabled])',
      )
      first?.focus()
    } else {
      triggerRef.current?.focus()
    }
  }, [open])

  return (
    <div className={className}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <button
          ref={triggerRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(o => !o)}
          className={`px-2.5 py-1.5 text-xs rounded transition-colors ${
            tone === 'danger'
              ? 'ring-1 ring-red-500/40 text-red-400 hover:bg-red-500/10'
              : 'themed-btn-secondary'
          }`}
        >
          <span aria-hidden className="inline-block mr-1.5 transition-transform" style={{
            transform: open ? 'rotate(90deg)' : 'none',
          }}>›</span>
          {open ? (openLabel ?? label) : label}
        </button>
        {hint && <span className="themed-text-muted text-[11px]">{hint}</span>}
      </div>
      {open && (
        <div ref={panelRef} id={panelId} className="mt-2">
          {children}
        </div>
      )}
    </div>
  )
}
